import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import type { CardBranding } from "../lib/types";

export default function BrandingSettings() {
  const [branding, setBranding] = useState<CardBranding | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api<{ branding: CardBranding }>("/branding").then((res) => setBranding(res.branding));
  }, []);

  if (!branding) return <div className="empty-state">Loading…</div>;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!branding) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await api<{ branding: CardBranding }>("/branding", {
        method: "PUT",
        body: {
          companyName: branding.companyName,
          primaryColor: branding.primaryColor,
          secondaryColor: branding.secondaryColor,
          textColor: branding.textColor,
          backgroundStyle: branding.backgroundStyle,
          cardTitle: branding.cardTitle,
          footerText: branding.footerText,
          legalText: branding.legalText,
        },
      });
      setBranding(res.branding);
      if (logoFile) {
        const fd = new FormData();
        fd.append("logo", logoFile);
        const logoRes = await api<{ branding: CardBranding }>("/branding/logo", { method: "POST", body: fd, isForm: true });
        setBranding(logoRes.branding);
      }
      setMessage("Branding updated. New cards and Wallet passes will use this branding automatically.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="topbar">
        <div>
          <h1 className="page-title">Branding</h1>
          <div className="page-subtitle">Centralized identity applied to every generated card and Wallet pass</div>
        </div>
      </div>

      {message && <div className="alert alert-info">{message}</div>}

      <form className="card" onSubmit={handleSubmit} style={{ maxWidth: 560 }}>
        <div className="field">
          <label>Company / Mall Name</label>
          <input value={branding.companyName} onChange={(e) => setBranding({ ...branding, companyName: e.target.value })} />
        </div>
        <div className="field">
          <label>Card Title</label>
          <input value={branding.cardTitle ?? ""} onChange={(e) => setBranding({ ...branding, cardTitle: e.target.value })} />
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Primary Color</label>
            <input type="color" value={branding.primaryColor} onChange={(e) => setBranding({ ...branding, primaryColor: e.target.value })} />
          </div>
          <div className="field">
            <label>Secondary Color</label>
            <input type="color" value={branding.secondaryColor} onChange={(e) => setBranding({ ...branding, secondaryColor: e.target.value })} />
          </div>
          <div className="field">
            <label>Text Color</label>
            <input type="color" value={branding.textColor} onChange={(e) => setBranding({ ...branding, textColor: e.target.value })} />
          </div>
        </div>
        <div className="field">
          <label>Footer Text (optional)</label>
          <input value={branding.footerText ?? ""} onChange={(e) => setBranding({ ...branding, footerText: e.target.value })} />
        </div>
        <div className="field">
          <label>Legal Text (optional)</label>
          <textarea rows={2} value={branding.legalText ?? ""} onChange={(e) => setBranding({ ...branding, legalText: e.target.value })} />
        </div>
        <div className="field">
          <label>Company Logo</label>
          {branding.logoUrl && <img src={branding.logoUrl} alt="Current logo" style={{ width: 64, height: 64, objectFit: "contain", marginBottom: 6 }} />}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
        </div>
        <button className="btn btn-gold" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save Branding"}
        </button>
      </form>
    </div>
  );
}
